/**
 * Shipping Service
 *
 * Provides methods for fetching shipping carriers and calculating shipping costs
 * for prescription deliveries. Integrates with the backend shipping API which
 * supports multiple carriers (Ameriship, ShipEngine/UPS/FedEx/USPS).
 *
 * @module shippingService
 */

import api from './api';

/**
 * Represents a shipping carrier option
 */
export interface ShippingCarrier {
  /** Unique identifier for the carrier (e.g., "se-11226", "ameriship") */
  id: string;
  /** Display name for the carrier (e.g., "UPS", "Ameriship") */
  name: string;
}

/**
 * Represents a shipping delivery option with pricing
 */
export interface ShippingOption {
  /** Unique identifier for the shipping option/service */
  id: string;
  /** Display name for the option (e.g., "Ground", "Priority Mail") */
  name: string;
  /** Cost in dollars (0 if free shipment applies) */
  totalCost: number;
  /** Original cost in dollars before free shipment discount */
  originalCost: number;
}

/**
 * Represents the user's shipping selection for a prescription
 */
/** One line for BUD-based ShipEngine package dimensions */
export interface ShippingPackageLine {
  pharmetikaId: string | number;
  quantity: string | number;
}

export function cartItemsToShippingLines(
  items: Array<{
    template_id?: string;
    product_id?: string;
    quantity: number;
    pharmetika_id?: string;
  }>
): ShippingPackageLine[] {
  return items.map(i => ({
    pharmetikaId: i.pharmetika_id || i.template_id || i.product_id || '0',
    quantity: i.quantity,
  }));
}

/** Reads pharmetika_id from API product objects (shape varies by endpoint). */
function pharmetikaIdFromProduct(product: unknown): string | undefined {
  if (product == null || typeof product !== 'object') {
    return undefined;
  }
  const id = (product as { pharmetika_id?: unknown }).pharmetika_id;
  if (id == null || id === '') {
    return undefined;
  }
  return String(id);
}

/**
 * Builds ShipEngine dimension lines from medication requests.
 * Accepts any cart/prescription row with optional nested `product` (typed loosely so
 * PrescriptionCartItem and API-shaped types both work).
 */
export function prescriptionMedicationRequestsToShippingLines(
  requests: ReadonlyArray<{
    quantity_authorized?: number | string;
    product?: unknown;
    template_id?: string;
    pharmetika_id?: string;
  }>
): ShippingPackageLine[] {
  return requests.map(r => ({
    pharmetikaId:
      (r.pharmetika_id && String(r.pharmetika_id)) ||
      pharmetikaIdFromProduct(r.product) ||
      r.template_id ||
      '0',
    quantity: r.quantity_authorized ?? 1,
  }));
}

export function enrollMedicationsToShippingLines(
  meds: Array<{ template_id?: string; quantity: number }>
): ShippingPackageLine[] {
  return meds.map(m => ({
    pharmetikaId: m.template_id || '0',
    quantity: m.quantity,
  }));
}

export interface ShippingSelection {
  /** Selected carrier ID */
  carrierId: string;
  /** Selected carrier display name */
  carrierName: string;
  /** Selected shipping option/service ID */
  optionId: string;
  /** Selected option display name */
  optionName: string;
  /** Final shipping cost in dollars */
  cost: number;
  /** Original cost before any discounts */
  originalCost: number;
  /** Whether free shipment was applied */
  isFreeShipment: boolean;
  /** Type of free shipment if applicable ("global" or "clinic_specific") */
  freeShipmentType: string | null;
}

/**
 * Response from the get carriers API
 */
interface GetCarriersResponse {
  success: boolean;
  data: ShippingCarrier[];
  error?: string;
}

/**
 * Response from the calculate shipping cost API
 */
export interface PackageDimensions {
  unit: string;
  length: number;
  width: number;
  height: number;
}

interface CalculateShippingCostResponse {
  success: boolean;
  data: ShippingOption[];
  isFreeShipment: boolean;
  freeShipmentType: string | null;
  patientState: string | null;
  packageDimensions?: PackageDimensions | null;
  requiresColdChain?: boolean;
  error?: string;
}

/**
 * Response from save shipping selection API
 */
interface SaveShippingResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Shipping service providing carrier and cost calculation methods
 */
export const shippingService = {
  /**
   * Fetches available shipping carriers for a clinic
   *
   * @param {string} clinicId - The clinic's UUID
   * @returns {Promise<{success: boolean; data?: ShippingCarrier[]; error?: string}>}
   *          Object containing success status and either carrier list or error message
   *
   * @example
   * const result = await shippingService.getCarriers('clinic-uuid');
   * if (result.success) {
   *   console.log(result.data); // [{ id: 'ameriship', name: 'Ameriship' }, ...]
   * }
   */
  async getCarriers(
    clinicId: string
  ): Promise<{ success: boolean; data?: ShippingCarrier[]; error?: string }> {
    try {
      const response = await api.get<GetCarriersResponse>(
        `/payments/clinics/${clinicId}/shipping/carriers`
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      // Handle both string and object error formats
      const errorData = response.data.error;
      const errorMessage = typeof errorData === 'object' ? errorData?.message : errorData;
      return {
        success: false,
        error: errorMessage || 'Failed to fetch carriers',
      };
    } catch (error: any) {
      console.error('Failed to fetch shipping carriers:', error);
      // The axios interceptor transforms errors into ApiError with message property
      return {
        success: false,
        error: error.message || 'Failed to fetch shipping carriers',
      };
    }
  },

  /**
   * Calculates shipping costs for a patient and carrier combination
   *
   * This method fetches available delivery options and their costs from the
   * selected carrier. If the patient's state qualifies for free shipment
   * (either globally or clinic-specific), all options will show $0 cost
   * but retain the original cost for reference.
   *
   * @param {string} clinicId - The clinic's UUID
   * @param {string} patientId - The patient's UUID
   * @param {string} carrierId - The selected carrier ID (e.g., "ameriship", "se-11226")
   * @returns {Promise<{success: boolean; data?: ShippingOption[]; isFreeShipment?: boolean; freeShipmentType?: string | null; patientState?: string | null; error?: string}>}
   *          Object containing success status, shipping options, free shipment flags, or error
   *
   * @example
   * const result = await shippingService.calculateShippingCost('clinic-id', 'patient-id', 'ameriship');
   * if (result.success) {
   *   console.log(result.data); // [{ id: '1', name: 'Ground', totalCost: 0, originalCost: 9.99 }, ...]
   *   console.log(result.isFreeShipment); // true
   * }
   */
  async calculateShippingCost(
    clinicId: string,
    patientId: string,
    carrierId: string,
    lines?: ShippingPackageLine[]
  ): Promise<{
    success: boolean;
    data?: ShippingOption[];
    isFreeShipment?: boolean;
    freeShipmentType?: string | null;
    patientState?: string | null;
    packageDimensions?: PackageDimensions | null;
    requiresColdChain?: boolean;
    error?: string;
  }> {
    try {
      const response = await api.post<CalculateShippingCostResponse>(
        `/payments/clinics/${clinicId}/shipping/calculate-shipping-cost`,
        {
          patientId,
          shippingProviderId: carrierId,
          ...(lines?.length
            ? { options: { lines } }
            : {}),
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          isFreeShipment: response.data.isFreeShipment,
          freeShipmentType: response.data.freeShipmentType,
          patientState: response.data.patientState,
          packageDimensions: response.data.packageDimensions ?? null,
          requiresColdChain: response.data.requiresColdChain ?? false,
        };
      }

      // Handle both string and object error formats
      const errorData = response.data.error;
      const errorMessage = typeof errorData === 'object' ? errorData?.message : errorData;
      return {
        success: false,
        error: errorMessage || 'Failed to calculate shipping cost',
      };
    } catch (error: any) {
      console.error('Failed to calculate shipping cost:', error);
      // The axios interceptor transforms errors into ApiError with message property
      return {
        success: false,
        error: error.message || 'Failed to calculate shipping cost',
      };
    }
  },

  /**
   * Saves shipping selection to a prescription
   *
   * Used when sending a payment link - the practitioner selects shipping
   * and this saves it to the prescription before sending the link to the patient.
   *
   * @param {string} prescriptionId - The prescription's UUID
   * @param {ShippingSelection} selection - The shipping selection to save
   * @returns {Promise<{success: boolean; error?: string}>}
   *          Object containing success status or error message
   *
   * @example
   * const result = await shippingService.saveShippingSelection('rx-id', {
   *   carrierId: 'ameriship',
   *   carrierName: 'Ameriship',
   *   optionId: '1',
   *   optionName: 'Ground',
   *   cost: 0,
   *   originalCost: 9.99,
   *   isFreeShipment: true,
   *   freeShipmentType: 'global'
   * });
   */
  async saveShippingSelection(
    prescriptionId: string,
    selection: ShippingSelection
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.patch<SaveShippingResponse>(
        `/payments/prescriptions/${prescriptionId}/shipping`,
        {
          shipping_carrier_id: selection.carrierId,
          shipping_carrier_name: selection.carrierName,
          shipping_service_id: selection.optionId,
          shipping_service_name: selection.optionName,
          shipping_cost_cents: Math.round(selection.cost * 100),
          shipping_original_cost_cents: Math.round(selection.originalCost * 100),
          is_free_shipment: selection.isFreeShipment,
          free_shipment_type: selection.freeShipmentType,
        }
      );

      if (response.data.success) {
        return { success: true };
      }

      // Handle both string and object error formats
      const errorData = response.data.error;
      const errorMessage = typeof errorData === 'object' ? errorData?.message : errorData;
      return {
        success: false,
        error: errorMessage || 'Failed to save shipping selection',
      };
    } catch (error: any) {
      console.error('Failed to save shipping selection:', error);
      // The axios interceptor transforms errors into ApiError with message property
      return {
        success: false,
        error: error.message || 'Failed to save shipping selection',
      };
    }
  },

  /**
   * Formats a shipping cost for display
   *
   * @param {number} cost - Cost in dollars
   * @param {boolean} isFreeShipment - Whether free shipment applies
   * @param {number} [originalCost] - Original cost before discount
   * @returns {string} Formatted cost string
   *
   * @example
   * formatCost(0, true, 9.99) // "FREE (was $9.99)"
   * formatCost(9.99, false) // "$9.99"
   */
  formatCost(cost: number, isFreeShipment: boolean, originalCost?: number): string {
    if (isFreeShipment && cost === 0) {
      return originalCost ? `FREE (was $${originalCost.toFixed(2)})` : 'FREE';
    }
    return `$${cost.toFixed(2)}`;
  },
};
